import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useSuperAdmin } from '../../hooks/useSuperAdmin';
import { Loader2 } from 'lucide-react';
import { NotFound } from './NotFound';

interface SuperAdminRouteProps {
  children: React.ReactNode;
}

/**
 * Protected route component that only allows super admins to access
 * Shows 404 page if user is not a super admin (to not reveal the route exists)
 */
export function SuperAdminRoute({ children }: SuperAdminRouteProps) {
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { isSuperAdmin, isLoading: isSuperAdminLoading } = useSuperAdmin();

  // Check authentication first
  useEffect(() => {
    async function checkAuth() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setIsAuthenticated(!!session);
        setIsAuthLoading(false);
      } catch (error) {
        console.error('Auth check error:', error);
        setIsAuthenticated(false);
        setIsAuthLoading(false);
      }
    }

    checkAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Show minimal loading state (don't reveal what we're checking)
  if (isAuthLoading || isSuperAdminLoading) {
    return (
      <div className="min-h-screen bg-[var(--theme-background)] flex items-center justify-center">
        <Loader2 size={24} strokeWidth={1.5} className="text-[var(--theme-primary)]/20 animate-spin" />
      </div>
    );
  }

  // Not authenticated OR not a super admin = show 404 (route doesn't exist)
  if (!isAuthenticated || !isSuperAdmin) {
    return <NotFound />;
  }

  return <>{children}</>;
}
